import { useEffect, useState, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { X, Check, Loader2, Boxes } from "lucide-react";
import { api } from "@/lib/api";
import { useUI } from "@/context/UIContext";
import { useAuth } from "@/context/AuthContext";

// Clean, non-overlapping descriptors so toggling one category doesn't falsely satisfy another.
const PRODUCT_NAME = {
  "Boots": "Hunting Boots", "Base Layers": "Merino Base Layer", "Insulated Jackets": "Insulated Puffy",
  "Rain Shells": "Rain Shell", "Packs": "Hunting Pack", "Binoculars": "Binoculars",
  "Rangefinders": "Rangefinder", "Sleep Systems": "Sleep System", "Tents": "Tent",
};

export function GearLockerModal() {
  const { gearOpen, setGearOpen } = useUI();
  const { isAuthed } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  const load = useCallback(async () => {
    try { setData(await api.gearReadiness()); } catch { /* ignore */ } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (gearOpen && isAuthed) { setLoading(true); load(); }
  }, [gearOpen, isAuthed, load]);

  const toggle = async (cat) => {
    if (saving) return;
    setSaving(cat.category);
    try {
      if (cat.owned) {
        await Promise.all(cat.item_ids.map((id) => api.deleteGear(id)));
      } else {
        await api.addGear({ category: cat.category, product_name: PRODUCT_NAME[cat.category] || cat.category, condition: "owned" });
      }
      setData(await api.gearReadiness());
    } catch { toast.error("Could not update gear."); } finally { setSaving(null); }
  };

  const close = () => setGearOpen(false);
  const readiness = data?.readiness ?? 0;

  return (
    <AnimatePresence>
      {gearOpen && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[80] bg-[#0d1410]/90 backdrop-blur-sm grid sm:place-items-center p-0 sm:p-6 overflow-y-auto" data-testid="gear-locker-modal">
          <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }}
            className="w-full max-w-lg bg-[#12190f] border border-bone/12 rounded-none sm:rounded-lg text-bone relative min-h-full sm:min-h-0 grain">
            <button onClick={close} className="absolute top-4 right-4 text-bone/50 hover:text-bone p-1 z-10" aria-label="Close" data-testid="gear-close"><X size={22} /></button>

            <div className="p-6 sm:p-8">
              <span className="inline-flex items-center gap-2 hr-overline text-blaze"><Boxes size={15} /> Gear Locker</span>
              <h2 className="font-serif text-3xl sm:text-4xl mt-1.5">Check off your kit.</h2>
              <p className="text-sm text-bone/60 mt-2">Mark the essentials you already own. Your hunt readiness climbs as your locker fills in.</p>

              <div className="flex items-center gap-3 mt-5">
                <div className="flex-1 h-2 rounded-full bg-bone/10 overflow-hidden">
                  <div className="h-full bg-blaze transition-all duration-500" style={{ width: `${readiness}%` }} data-testid="gear-readiness-bar" />
                </div>
                <span className="font-serif text-2xl tabular-nums" data-testid="gear-readiness-pct">{readiness}%</span>
              </div>

              {loading ? (
                <div className="flex items-center gap-2 text-bone/50 py-10 justify-center"><Loader2 className="animate-spin text-blaze" /> Loading your locker…</div>
              ) : (
                <div className="mt-6 space-y-2" data-testid="gear-categories">
                  {(data?.categories || []).map((c) => (
                    <button key={c.category} onClick={() => toggle(c)} disabled={saving === c.category}
                      className={`w-full flex items-center gap-3 border rounded-md px-4 py-3 text-left transition-colors ${c.owned ? "border-blaze/50 bg-blaze/10" : "border-bone/15 hover:border-bone/30"}`}
                      data-testid={`gear-cat-${c.category.replace(/[^a-zA-Z]+/g, "-").toLowerCase()}`}>
                      <span className={`w-5 h-5 rounded grid place-items-center shrink-0 ${c.owned ? "bg-blaze text-white" : "border border-bone/30"}`}>
                        {saving === c.category ? <Loader2 size={12} className="animate-spin" /> : c.owned ? <Check size={13} /> : null}
                      </span>
                      <span className={`text-sm ${c.owned ? "text-bone" : "text-bone/70"}`}>{c.category}</span>
                      <span className="ml-auto text-[11px] text-bone/40">{c.owned ? "Owned" : "Add"}</span>
                    </button>
                  ))}
                </div>
              )}

              <button onClick={close} className="w-full mt-6 bg-blaze text-white py-3 rounded-md font-semibold hover:bg-blaze-light transition-colors" data-testid="gear-done">Done</button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
